import type {
  EventDateType,
  EventDate,
  EventDescription,
  EventDateChildren,
  EventCalendarDayMap,
} from '~/composables/types/event'

const getDateType = (date: EventDate): EventDateType => {
  return date?.dateType || 'PUBLIC'
}

const getChildren = (date: EventDate): EventDateChildren[] => {
  return date?.children?.data || date?.children || []
}

const isMultiDate = (date: EventDate): boolean =>
  Boolean(date?.children?.length || date?.children?.data?.length)

// Total sessions of a multidate instance. getChildren returns only the
// additional sessions, so we add one to account for the parent date, which is
// also a session. Non-multidate dates have no session count (0).
const getSessionsCount = (date: EventDate): number =>
  isMultiDate(date) ? getChildren(date).length + 1 : 0

const getIsPrivate = (date: EventDate, strict = false) => {
  if (strict) {
    return getDateType(date) === 'PRIVATE'
  }

  return getDateType(date) !== 'PUBLIC'
}

const getIsPublic = (date: EventDate, strict = false) => {
  if (strict) {
    return getDateType(date) === 'PUBLIC'
  }
  return getDateType(date) !== 'PRIVATE'
}

const getDateIsOpen = (date: EventDate) => {
  return date.status === 'OPEN'
}

const getDateIsSoldOut = (date: EventDate) => {
  return date.status === 'SOLD_OUT'
}

const getAvailableTicketsQuantity = (date: EventDate | undefined) => {
  return date?.availableTicketsQuantity || 0
}

const getPrivateMinTickets = (date?: EventDate) => {
  return date?.privateMinTickets || 1
}

const getPrivateMaxTickets = (date: EventDate) => {
  return date?.privateMaxTickets || 1
}

const getPrice = (date: EventDate) => {
  return date?.product?.price || date?.product?.data?.price || null
}

const getPriceAmount = (date?: EventDate): number => {
  return (
    (date?.product?.price?.amount as number) ||
    (date?.product?.data?.price?.amount as number) ||
    0
  )
}

// A date is on sale when its price is below the original price
// (covers both event-wide and date-specific promotions).
const getHasPromotion = (date?: EventDate): boolean =>
  Number(getPriceAmount(date)) <
  Number(date?.product?.original_price?.amount || 0)

const getMinimumPrivatePriceAmount = (date?: EventDate): number =>
  getPrivateMinTickets(date) * getPriceAmount(date)

const getStartTime = (date: EventDate) => {
  return date?.start || ''
}

const getEndTime = (date: EventDate) => {
  return date?.end || ''
}

const getSoldOutDatesList = (dates: EventDate[]) => {
  return toValue(dates).filter((date: EventDate) =>
    eventDateGetters.getDateIsSoldOut(date)
  )
}

const getNonSoldOutDatesList = (dates: EventDate[]) => {
  return toValue(dates).filter(
    (date: EventDate) => !eventDateGetters.getDateIsSoldOut(date)
  )
}
const getFormattedEventDatePrice = (
  event: EventDescription,
  selectedTime: EventDate | null = null
) => {
  if (selectedTime !== null) {
    return eventGetters.getPrice(selectedTime?.product)
  }

  const defaultPrice = eventGetters.getDefaultFormattedPrice(event)

  if (defaultPrice) {
    return defaultPrice
  }

  return eventGetters.getDefaultFormattedPrice(event)
}

export function areAllDatesSoldOut(dates: EventDate[]) {
  return getNonSoldOutDatesList(toValue(dates)).length === 0
}

export function areAnyDatesAvailable(dates: EventDate[]) {
  return toValue(dates).length > 0
}

/**
 * Gets allowed days from a calendar day map that have available dates and tickets
 * @param calendarDayMap - The calendar day map to check
 * @returns Array of day keys that have available dates and tickets
 */
export function getAllowedDays(
  calendarDayMap: EventCalendarDayMap | Record<any, any> | undefined
): string[] {
  if (!calendarDayMap) return []

  return Object.keys(calendarDayMap)
    .filter(
      (day) =>
        calendarDayMap[day]?.dates?.some(
          (date: EventDate) => date.status === 'OPEN'
        ) && calendarDayMap[day].tickets_left > 0
    )
    .sort()
}

/**
 * Checks if there are any available dates in a calendar day map
 * @param calendarDayMap - The calendar day map to check
 * @returns True if there are available dates, false otherwise
 */
export function areThereDatesAvailable(
  calendarDayMap: EventCalendarDayMap | Record<any, any> | undefined
): boolean {
  return getAllowedDays(calendarDayMap).length > 0
}

export function getFirstAvailableDate(
  calendarDayMap: EventCalendarDayMap | Record<any, any> | undefined
): string | undefined {
  return getAllowedDays(calendarDayMap)?.[0]
}

export const eventDateGetters = {
  getIsPublic,
  getDateType,
  getIsPrivate,
  getDateIsOpen,
  getStartTime,
  getEndTime,
  getAvailableTicketsQuantity,
  getPrivateMinTickets,
  getPrivateMaxTickets,
  getPrice,
  getPriceAmount,
  getHasPromotion,
  getMinimumPrivatePriceAmount,
  getDateIsSoldOut,
  getSoldOutDatesList,
  getNonSoldOutDatesList,
  getFormattedEventDatePrice,
  areAllDatesSoldOut,
  areAnyDatesAvailable,
  getAllowedDays,
  areThereDatesAvailable,
  getChildren,
  isMultiDate,
  getSessionsCount,
  getFirstAvailableDate,
}
